import React, { createContext, useContext, useMemo, useEffect, useState, useRef } from 'react'
import io from 'socket.io-client'
import { SOCKET_URL, getParticipantToken } from '../utils/api'

const SocketContext = createContext(null)

/**
 * SocketProvider — notification-only socket connection.
 *
 * The socket does exactly 2 things:
 * 1. Joins a conference room to receive notifications
 * 2. Receives "conference-update" events (lightweight pings)
 *
 * It NEVER carries data. All data flows through HTTP.
 */
export const SocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false)

  const socket = useMemo(
    () =>
      io(SOCKET_URL, {
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 5000,
        transports: ['websocket', 'polling'],
        // Send the HttpOnly admin session cookie with the handshake so the
        // server can authenticate admins (it reads the cookie from the handshake
        // headers, not auth.token). Participants authenticate separately via
        // join-room using their participant token.
        withCredentials: true,
      }),
    []
  )

  useEffect(() => {
    const onConnect = () => setConnected(true)
    const onDisconnect = () => setConnected(false)
    // Bridge "conference-update" pings into a same-origin window event, so
    // simple local UIs (e.g. ParticipantView's "mute-all" listener) can react
    // without having to drill the socket reference through props.
    const onUpdate = (payload) => {
      try {
        if (payload?.type === 'mute-all') {
          window.dispatchEvent(new CustomEvent('cc:mute-all'))
        }
        if (['mute-participant', 'request-unmute', 'stop-participant-camera', 'technical-prompt'].includes(payload?.type)) {
          window.dispatchEvent(new CustomEvent(`cc:${payload.type}`, { detail: payload }))
        }
      } catch {}
    }
    // Moderator-directed participant navigation is a one-shot event. Keep it
    // separate from conference-update so it does not trigger an unnecessary
    // full state refetch or keep participants locked on the selected tab.
    const onParticipantPanelFocus = (payload) => {
      try {
        if (payload?.tab === 'pinnwand' || payload?.tab === 'aktuell') {
          window.dispatchEvent(new CustomEvent('cc:participant-panel-focus', {
            detail: { tab: payload.tab, requestedAt: payload.requested_at || null },
          }))
        }
      } catch {}
    }

    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)
    socket.on('conference-update', onUpdate)
    socket.on('participant-panel-focus', onParticipantPanelFocus)
    // React's development StrictMode deliberately runs effect cleanup/setup
    // twice with the same memoised Socket instance. cleanup disconnects the
    // owned transport, so setup must explicitly make that instance live again.
    if (!socket.connected) socket.connect()
    setConnected(socket.connected)

    return () => {
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
      socket.off('conference-update', onUpdate)
      socket.off('participant-panel-focus', onParticipantPanelFocus)
      // This provider creates and owns the transport. Route changes previously
      // removed listeners but left the Socket.IO client connected forever,
      // accumulating idle server connections after every room visit.
      socket.disconnect()
    }
  }, [socket])

  const value = useMemo(() => ({ socket, connected }), [socket, connected])

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}

export const useSocket = () => {
  const ctx = useContext(SocketContext)
  if (!ctx) throw new Error('useSocket must be used within SocketProvider')
  return ctx
}

/**
 * Hook to join a conference room for notifications.
 * Call once when the participant/admin enters the conference.
 *
 * Auto re-joins on socket `connect` events so a reconnect after a wifi
 * blip immediately restores the subscription — without this the client
 * sat outside `conf_<uuid>` until the next 15s state-refetch tick and
 * would silently miss short notifications (vote-started, broadcast etc.).
 */
export function buildJoinRoomPayload (conferenceId, participantId, participantToken) {
  if (participantId) {
    return {
      conferenceId,
      audience: 'participant',
      participantId,
      participantToken: participantToken || undefined,
    }
  }
  return { conferenceId, audience: 'admin' }
}

export function useJoinRoom(conferenceId, participantId = null) {
  const { socket } = useSocket()
  const conferenceIdRef = useRef(conferenceId)
  conferenceIdRef.current = conferenceId

  useEffect(() => {
    if (!conferenceId || !socket) return

    // The provider identity, not ambient localStorage, selects the audience.
    // This matters when an admin cookie and participant data coexist in one
    // browser: admin views omit participant intent; participant views fail
    // closed against their explicit id/token pair.
    const emitJoin = () => {
      const participantToken = participantId ? getParticipantToken(conferenceId) : null
      socket.emit('join-room', buildJoinRoomPayload(conferenceId, participantId, participantToken))
    }
    emitJoin()

    const onConnect = () => {
      // Re-emit on every reconnect — the server-side room is keyed on
      // socket.id which changes when socket.io reconnects.
      const cur = conferenceIdRef.current
      if (cur === conferenceId) emitJoin()
    }
    socket.on('connect', onConnect)

    // A participant token can be renewed without the Socket.IO transport
    // disconnecting (e.g. localStorage was cleared, then /join recovered it).
    // Re-join immediately with the new credential so live notifications and
    // the fast data feed are restored together with HTTP access.
    const onTokenChanged = (event) => {
      if (participantId && (!event?.detail?.conferenceId || event.detail.conferenceId === conferenceId)) emitJoin()
    }
    window.addEventListener('cc:participant-token-changed', onTokenChanged)

    return () => {
      socket.off('connect', onConnect)
      window.removeEventListener('cc:participant-token-changed', onTokenChanged)
      socket.emit('leave-room', { conferenceId })
    }
  }, [socket, conferenceId, participantId])
}

export default SocketContext
